import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  serverEndpoint = environment.apiUrl;
  private loginStatus = new Subject<boolean>();
  loginStatus$ = this.loginStatus.asObservable();
  
  // Use multiple token keys for compatibility
  private readonly tokenKeys = ['user-access', 'token', 'user-token-goldBharatFirst'];
  private isLoginSubject = new BehaviorSubject<boolean>(this.hasToken());

  constructor(private http: HttpClient) {}
  
  setLoginStatus(isLoggedIn: boolean) {
    this.loginStatus.next(isLoggedIn);
  }
  
  registerUser(data: any) {
    const endPoint = `auth/register`;
    return this.http.post(this.serverEndpoint + endPoint, data);
  }
  
  pinCodeArea(pincode: number) {
    const endPoint = `pincode/${pincode}`;
    return this.http.get(this.serverEndpoint + endPoint);
  }
  
  sendOpt(data: any) {
    const endPoint = `auth/sendotp`;
    return this.http.post(this.serverEndpoint + endPoint, data);
  }
  
  verifyOtp(data: any) {
    const endPoint = `auth/verifyotp`;
    return this.http.post(this.serverEndpoint + endPoint, data);
  }
  
  loginUser(data: any) {
    const endPoint = `auth/login`;
    return this.http.post(this.serverEndpoint + endPoint, data).pipe(
      tap((response: any) => {
        if (response.token) {
          this.login(response.token);
          // Also store user data if available
          if (response.user) {
            localStorage.setItem('user-data', JSON.stringify(response.user));
            localStorage.setItem('user-name', response.user.name || '');
            localStorage.setItem('user-rolename', response.user.role || '');
          }
        }
      })
    );
  }
  
  // FIXED: Get user details with proper headers
  userDetailGet() {
    const endPoint = `getProfile`;
    const token = this.getToken();
    
    if (!token) {
      return throwError(() => new Error('No token found'));
    }
    
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });
    
    return this.http.get(this.serverEndpoint + endPoint, { headers }).pipe(
      tap((response: any) => {
        if (response.success && response.data) {
          // Update user data in localStorage
          localStorage.setItem('user-data', JSON.stringify(response.data));
          localStorage.setItem('user-name', response.data.name || '');
          localStorage.setItem('user-rolename', response.data.role || '');
        }
      }),
      catchError(this.handleError)
    );
  }
  
  // Helper method to get token from any of the possible keys
  private getToken(): string | null {
    for (const key of this.tokenKeys) {
      const token = localStorage.getItem(key);
      if (token) return token;
    }
    return null;
  }
  
  // Check if user is logged in
  isLoggedIn(): boolean {
    return !!this.getToken();
  }
  
  // For backward compatibility
  hasToken(): boolean {
    return this.isLoggedIn();
  }
  
  isMobile(): boolean {
    const userAgent = navigator.userAgent || navigator.vendor;
    return /android|iPad|iPhone|iPod/i.test(userAgent) || window.innerWidth <= 768;
  }
  
  deleteUser(userData: any): Observable<any> {
    const token = this.getToken();
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`
    });
    return this.http.post<any>(`${this.serverEndpoint}/userTerminate`, userData, { headers });
  }
  
  // Get the current login status as an observable
  public isLoggedInFooter() {
    return this.isLoginSubject.asObservable();
  }

  // Log in method - store token in all possible keys for compatibility
  public login(token: string): void {
    for (const key of this.tokenKeys) {
      localStorage.setItem(key, token);
    }
    this.isLoginSubject.next(true);
    this.setLoginStatus(true);
  }

  // Log out method - remove all token keys
  public logout(): void {
    for (const key of this.tokenKeys) {
      localStorage.removeItem(key);
    }
    localStorage.removeItem('user-data');
    localStorage.removeItem('user-name');
    localStorage.removeItem('user-rolename');
    this.isLoginSubject.next(false);
    this.setLoginStatus(false);
  }
  
  private handleError(error: any) {
    console.error('API Error:', error);
    
    if (error.status === 400 && error.error?.message === 'Invalid Token') {
      this.logout();
    }
    
    return throwError(() => error);
  }
}